/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Virtual events (DynamoDB) — list, CRUD, and presigned banner upload.
 * @module logic/virtualEvents
 */

import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import {
  AUDIENCIA_MEMBERS_ONLY,
  AUDIENCIA_NON_MEMBERS_ONLY,
  HTTP_CONFLICT,
  fetchUserRoleData,
  logDevError,
  getAuthJsonHeaders,
  normalizeAudienciaFromApi,
  resolveExpiredToken,
  showEventCreatedSuccess,
  uploadEventBannerFromDataUrl,
} from "../../utils/eventApi.js";
import {
  TIME_COLON_INDEX,
  TIME_INPUT_HH_MM_LENGTH,
  filterCalendarDayEventsForEventsList,
} from "../../utils/eventDateFilters.js";
import {
  filterNonCancelledEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import { getUser } from "../user";

const VIRTUAL_EVENT_NOT_FOUND = "Virtual event not found";
const DATE_FORMAT_YYYY_MM_DD = "YYYY-MM-DD";

export { showEventCreatedSuccess };

/**
 * Normalizes a time string for `input type="time"` (HH:mm). The API may return HH:mm:ss.
 * @param {string|null|undefined} value - Raw time from the API or form.
 * @returns {string|null} First five characters when parseable as HH:mm, otherwise trimmed string or null.
 */
const normalizeTimeForInput = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const s = String(value).trim();
  if (s.length >= TIME_INPUT_HH_MM_LENGTH && s[TIME_COLON_INDEX] === ":") {
    return s.slice(0, TIME_INPUT_HH_MM_LENGTH);
  }
  return s || null;
};

/**
 * Formats a date-like value to `YYYY-MM-DD` in UTC for HTML date inputs.
 * @param {import("dayjs").Dayjs} dayjs - dayjs instance (injected to avoid circular imports).
 * @param {string|Date|null|undefined} value - API date string or similar.
 * @returns {string|null}
 */
const formatDateField = (dayjs, value) =>
  value ? dayjs(value).utc().format(DATE_FORMAT_YYYY_MM_DD) : null;

export { normalizeAudienciaFromApi };

/**
 * Maps API `notificacionPrevia` to `0` (sí) / `1` (no), else `null`.
 * @param {string|number|null|undefined} value
 * @returns {0|1|null}
 */
const normalizeNotificacionPreviaFromApi = (value) => {
  if (value === AUDIENCIA_MEMBERS_ONLY || value === "0") {
    return AUDIENCIA_MEMBERS_ONLY;
  }
  if (value === AUDIENCIA_NON_MEMBERS_ONLY || value === "1") {
    return AUDIENCIA_NON_MEMBERS_ONLY;
  }
  return null;
};

/**
 * @param {unknown} value
 * @returns {number|null}
 */
const normalizeCostPayload = (value) => {
  if (value === "" || value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * @param {Record<string, unknown>} eventData
 * @returns {Record<string, unknown>}
 */
const buildVirtualEventCoreFields = (eventData) => ({
  userId: eventData.userId,
  nombre: eventData.nombre,
  descripcion: eventData.descripcion ?? null,
  fechaEvento: eventData.fechaEvento,
  hora: eventData.hora || null,
  duracion: eventData.duracion || null,
  audiencia: normalizeAudienciaFromApi(eventData.audiencia),
});

const buildVirtualEventAvailabilityFields = (eventData) => ({
  fechaDisponibleSocios: eventData.fechaDisponibleSocios || null,
  fechaDisponibleNoSocios: eventData.fechaDisponibleNoSocios || null,
  fechaLimiteRegistro: eventData.fechaLimiteRegistro || null,
  tiempoMinimoAsistencia: eventData.tiempoMinimoAsistencia ?? null,
  notificacionPrevia: normalizeNotificacionPreviaFromApi(
    eventData.notificacionPrevia,
  ),
  costoSocios: normalizeCostPayload(eventData.costoSocios),
  costoNoSocios: normalizeCostPayload(eventData.costoNoSocios),
  activo: eventData.activo ?? true,
  imagenBanner: eventData.imagenBanner || null,
  nombreImagen: eventData.nombreImagen || undefined,
});

const buildVirtualEventSubmitBody = (eventData) => ({
  ...buildVirtualEventCoreFields(eventData),
  ...buildVirtualEventAvailabilityFields(eventData),
});

/**
 * Fetches permission payload for the user and updates app permission state.
 * @param {string} userId
 * @param {Function} changePermissions
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<void>}
 */
export const getUserRoleData = fetchUserRoleData;

/**
 * Fetches active virtual events for the public Events page
 * (`GET /virtualEvent/user/{userId}/active`).
 * @param {string} userId
 * @param {object} user
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export const getVirtualEventsForEventsList = async (userId, user, navigate) => {
  if (!userId) {
    return [];
  }

  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}virtualEvent/user/${userId}/active`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getVirtualEventsForEventsList(userId, user, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (!Array.isArray(data)) {
      return [];
    }
    return filterCalendarDayEventsForEventsList(data, user);
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Lists every virtual event in DynamoDB (`GET /virtualEvent/all`) for admin / collaborator screens.
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export const getAllVirtualEvents = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}virtualEvent/all`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getAllVirtualEvents(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    const list = Array.isArray(data) ? data : [];
    return sortEventsFuturesFirst(filterNonCancelledEvents(list));
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Loads permissions, current user, and the full virtual-events list for the management list page.
 * @param {string} userId
 * @param {Function} changePermissions
 * @param {import("react-router").NavigateFunction} navigate
 * @param {Function} setUser
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export const getVirtualEventsPageData = async (
  userId,
  changePermissions,
  navigate,
  setUser,
) => {
  try {
    await getUserRoleData(userId, changePermissions, navigate);
    const userData = await getUser(userId, navigate);
    setUser(userData);
    return await getAllVirtualEvents(navigate);
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Loads one virtual event and maps API fields into form-friendly shapes.
 * @param {string} eventId
 * @param {import("react-router").NavigateFunction} navigate
 * @param {import("dayjs").Dayjs} dayjs
 * @param {Function} setFormData
 * @returns {Promise<void>}
 */
export const getVirtualEventDetails = async (
  eventId,
  navigate,
  dayjs,
  setFormData,
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}virtualEvent/${eventId}`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getVirtualEventDetails(eventId, navigate, dayjs, setFormData);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (data?.message === VIRTUAL_EVENT_NOT_FOUND) {
      return;
    }
    if (!response.ok) {
      return;
    }

    const dataForForm = { ...data };
    delete dataForForm.enlaceReunionMicrosoftTeams;
    delete dataForForm.costo;

    setFormData({
      ...dataForForm,
      costoSocios: data.costoSocios ?? data.costo ?? null,
      costoNoSocios: data.costoNoSocios ?? data.costo ?? null,
      fechaEvento: formatDateField(dayjs, data.fechaEvento),
      fechaDisponibleSocios: formatDateField(dayjs, data.fechaDisponibleSocios),
      fechaDisponibleNoSocios: formatDateField(
        dayjs,
        data.fechaDisponibleNoSocios,
      ),
      fechaLimiteRegistro: formatDateField(dayjs, data.fechaLimiteRegistro),
      hora: normalizeTimeForInput(data.hora),
      audiencia: normalizeAudienciaFromApi(data.audiencia),
      notificacionPrevia: normalizeNotificacionPreviaFromApi(
        data.notificacionPrevia,
      ),
    });
  } catch (error) {
    console.error(error);
  }
};

/**
 * Creates or updates a virtual event (`POST /virtualEvent` or `PUT /virtualEvent/{eventId}`).
 * @param {Record<string, unknown>} eventData
 * @param {string|undefined|null} eventId
 * @param {import("react-router").NavigateFunction} navigate
 * @param {Function} setSaving
 * @param {Function} setEventData
 * @param {Function} setHasChanges
 * @returns {Promise<{ ok: boolean, wasCreate?: boolean, event?: Record<string, unknown> }>}
 */
export const submitVirtualEvent = async (
  eventData,
  eventId,
  navigate,
  setSaving,
  setEventData,
  setHasChanges,
) => {
  setSaving(true);
  const wasCreate = !eventId;
  const { Buffer } = await import("buffer");

  try {
    const body = buildVirtualEventSubmitBody(eventData);
    const url = eventId
      ? `${process.env.REACT_APP_ROOT_API}virtualEvent/${eventId}`
      : `${process.env.REACT_APP_ROOT_API}virtualEvent`;
    const method = eventId ? "PUT" : "POST";
    const response = await fetch(url, {
      method,
      headers: getAuthJsonHeaders(),
      body: JSON.stringify(body),
    });

    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      submitVirtualEvent(
        eventData,
        eventId,
        navigate,
        setSaving,
        setEventData,
        setHasChanges,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { ok: false };
      }
      return tokenResult.result;
    }

    if (response.ok && data.event) {
      if (data.presignedUrl && eventData.imagenBanner) {
        await uploadEventBannerFromDataUrl(
          data.presignedUrl,
          eventData.imagenBanner,
          eventData.formatoImagen,
          Buffer,
        );
      }
      setEventData(data.event);
      setHasChanges(false);
      return { ok: true, wasCreate, event: data.event };
    }
    return { ok: false };
  } catch (error) {
    console.error("Error submitting virtual event:", error);
    return { ok: false };
  } finally {
    setSaving(false);
  }
};

/**
 * Prepares the virtual-event form: refreshes permissions, then loads the event when `eventId` is provided.
 * @param {string|undefined|null} eventId
 * @param {string} userId
 * @param {Function} changePermissions
 * @param {import("react-router").NavigateFunction} navigate
 * @param {import("dayjs").Dayjs} dayjs
 * @param {Function} setFormData
 * @param {Function} setIsLoading
 * @returns {Promise<void>}
 */
export const initializeVirtualEventForm = async (
  eventId,
  userId,
  changePermissions,
  navigate,
  dayjs,
  setFormData,
  setIsLoading,
) => {
  try {
    setIsLoading(true);
    await getUserRoleData(userId, changePermissions, navigate);
    if (eventId) {
      await getVirtualEventDetails(eventId, navigate, dayjs, setFormData);
    }
  } catch (error) {
    console.error("Error initializing virtual event form:", error);
  } finally {
    setIsLoading(false);
  }
};

/**
 * Backend member tier for virtual/B2B pricing (`tipoDeUsuario` `"2"` or `"3"`).
 * @param {string|undefined|null} tipoDeUsuario
 * @returns {boolean}
 */
export const isVirtualEventMemberUserType = (tipoDeUsuario) =>
  tipoDeUsuario === "2" || tipoDeUsuario === "3";

/**
 * Resolved MXN amount for the current user (mirrors backend registration pricing).
 * @param {Record<string, unknown>} event
 * @param {{ tipoDeUsuario?: string|null }|null|undefined} user
 * @returns {number}
 */
export const getVirtualEventResolvedPrice = (event, user) => {
  const raw = isVirtualEventMemberUserType(user?.tipoDeUsuario)
    ? event?.costoSocios
    : event?.costoNoSocios;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * Loads one virtual event for read-only use (e.g. Teams link on registration modal).
 * @param {string} eventId
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<Record<string, unknown>|null>}
 */
export const fetchVirtualEventById = async (eventId, navigate) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}virtualEvent/${eventId}`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      fetchVirtualEventById(eventId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (!response.ok || data?.message === VIRTUAL_EVENT_NOT_FOUND) {
      return null;
    }
    return typeof data === "object" && data != null ? data : null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Registers the current user for a virtual event (`POST /attendee/virtualEvent`).
 * @param {string} idVirtualEvent
 * @param {import("react-router").NavigateFunction} navigate
 * @param {{ nombre?: string, telefono?: string, codigoPais?: string }} [attendee]
 * @returns {Promise<{ success?: boolean, data?: unknown, error?: string, already?: boolean }>}
 */
export const registerForVirtualEvent = async (
  idVirtualEvent,
  navigate,
  attendee,
) => {
  const idUsuario = localStorage.getItem("userId");
  const body = {
    ...(attendee ?? {}),
    idVirtualEvent,
    idUsuario,
  };
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(body),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/virtualEvent`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      registerForVirtualEvent(idVirtualEvent, navigate, attendee),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { success: false, error: "auth" };
      }
      return tokenResult.result;
    }
    if (response.ok && data?.data) {
      return { success: true, data: data.data };
    }
    if (response.status === HTTP_CONFLICT && data?.data) {
      return { success: true, data: data.data, already: true };
    }
    return {
      success: false,
      error: data?.message || "Virtual event registration failed",
    };
  } catch (error) {
    logDevError(error);
    return {
      success: false,
      error: apiMessageOrGeneric(error?.message, "NETWORK_ERROR"),
    };
  }
};

/**
 * Fetches all virtual events by year.
 * @async
 * @param {string} year - Year to filter events by
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of virtual events by year
 */
export const getVirtualEventsByYear = async (year, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}virtualEvent/year/${year}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getVirtualEventsByYear(year, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      return data;
    } else {
      return [];
    }
  } catch (error) {
    console.error(error);
    return [];
  }
};
